import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import IconDone from '../../components/icons/IconDone';
import IconWrong from '../../components/icons/IconWrong';
import IconKeys from '../../components/icons/IconKeys';
import IconCopyUrl from '../../components/icons/IconCopyUrl';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import IconDelete from '../../components/icons/IconDelete';
import { getNotificationType } from '../../variables/notificationTypes';
import * as commApi from '../communications/api';
import AssignNewCommunicationModal from '../communications/AssignNewCommunicationModal';
import * as rescheduleApi from './rescheduleApi';
import RescheduleResponseModal from './RescheduleResponseModal';
import useDayParts from '../../hooks/useDayParts';
import wsManager from '../../services/wsManager';
import '../../css/platform-access-modal.css';
import '../../css/communications.css';
import '../../css/reschedule.css';

// "Платформа" tab - only rendered once a student has a UserPlatform account
// (see StudentCard.jsx). Two sections: an "actions" panel (account-level
// operations the teacher can trigger - password reset today, extensible for
// more later, same idea as this whole app's other panels) above the
// pending-approval queue (blur-reveal requests today, designed to carry
// other TeacherApprovalRequest types later with no UI change needed here -
// the list is generic). Self-fetches `platformUser` independently rather
// than receiving it as a prop from StudentCard.jsx (which already has one)
// - matches this module's established "self-fetching via useParams"
// convention (see StudentCourses.jsx/StudentWords.jsx).
export default function StudentPlatform() {
   const { id } = useParams();
   const navigate = useNavigate();
   const lang = language[language.lang].platformTab;
   const topLang = language[language.lang];

   const [requests, setRequests] = useState([]);
   const [status, setStatus] = useState('loading');
   const [platformUser, setPlatformUser] = useState(null);
   const [resetLink, setResetLink] = useState('');
   const [resetting, setResetting] = useState(false);
   const [resetError, setResetError] = useState('');
   const [copied, setCopied] = useState(false);
   const [togglingActive, setTogglingActive] = useState(false);
   const [activeError, setActiveError] = useState('');

   const [notifications, setNotifications] = useState([]);
   const [templates, setTemplates] = useState([]);
   const [showPicker, setShowPicker] = useState(false);
   const [pickerValue, setPickerValue] = useState('');
   const [showCreateModal, setShowCreateModal] = useState(false);

   const [negotiationItems, setNegotiationItems] = useState([]);
   const [rescheduleModalItem, setRescheduleModalItem] = useState(null);
   const [upcomingLessonIds, setUpcomingLessonIds] = useState([]);
   const dayParts = useDayParts();

   const loadNegotiations = () => {
      Promise.all([
         rescheduleApi.getPendingRescheduleRequests(id).catch(() => ({ data: [] })),
         rescheduleApi.getPendingCancelRequests(id).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes]) => {
         const reschedules = (Array.isArray(rescheduleRes.data) ? rescheduleRes.data : []).map((r) => ({ type: 'reschedule', id: r.id, raw: r }));
         const cancels = (Array.isArray(cancelRes.data) ? cancelRes.data : []).map((r) => ({ type: 'cancel', id: r.id, raw: r }));
         setNegotiationItems([...reschedules, ...cancels]);
      });
   };

   const loadNotifications = () => {
      commApi.getStudentNotifications(id).then(res => {
         setNotifications(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {});
   };

   const load = () => {
      axios.get('/api/teacher-approval-requests', { params: { student: id }, withCredentials: true })
         .then(response => {
            setRequests(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('done');
         });
   }

   useEffect(() => {
      load();
      loadNotifications();
      loadNegotiations();
      // Same "subscribe to every upcoming lesson's topic" approach as
      // StudentCard.jsx's header badge, so this list also updates live
      // instead of only refreshing after the teacher's own in-app actions.
      axios.get('/api/events?student=' + id + '&dateFrom=' + new Date().toISOString().slice(0, 10), { withCredentials: true })
         .then(response => {
            const events = Array.isArray(response.data) ? response.data : [];
            setUpcomingLessonIds([...new Set(events.map(ev => ev.lesson && ev.lesson.id).filter(Boolean))]);
         })
         .catch(() => setUpcomingLessonIds([]));
      axios.get('/api/platform-users', { params: { studentId: id }, withCredentials: true })
         .then(response => {
            const data = response.data;
            const user = Array.isArray(data) ? data[0] : data;
            setPlatformUser(user || null);
         })
         .catch(() => setPlatformUser(null));
      setResetLink('');
      setResetError('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   useEffect(() => {
      if(!upcomingLessonIds.length) return;
      const handler = () => loadNegotiations();
      upcomingLessonIds.forEach(lessonId => wsManager.subscribe('/topic/reschedule/' + lessonId, handler));
      return () => upcomingLessonIds.forEach(lessonId => wsManager.unsubscribe('/topic/reschedule/' + lessonId, handler));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [upcomingLessonIds]);

   const openPicker = () => {
      commApi.getNotifications().then(res => setTemplates(Array.isArray(res.data) ? res.data : [])).catch(() => {});
      setPickerValue('');
      setShowPicker(true);
   };
   const handleAssignExisting = () => {
      if (!pickerValue) return;
      commApi.assignNotification(id, pickerValue).then(() => {
         setShowPicker(false);
         loadNotifications();
      }).catch(err => console.log(err));
   };
   const handleUnassign = (studentNotificationId) => {
      commApi.unassignNotification(studentNotificationId).then(loadNotifications).catch(err => console.log(err));
   };
   const handleAssignedNew = () => {
      setShowCreateModal(false);
      loadNotifications();
   };

   // Resolved requests never come back from this endpoint (server-side
   // filters to PENDING only) - approving/rejecting here just means "reload
   // and it's naturally gone", no client-side removal bookkeeping needed.
   const handleApprove = (requestId) => {
      axios.post('/api/teacher-approval-requests/' + requestId + '/approve', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }
   const handleReject = (requestId) => {
      axios.post('/api/teacher-approval-requests/' + requestId + '/reject', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }

   // Same "raw link back in the API response, teacher copies and hands it
   // to the student themselves" shape as account creation
   // (PlatformAccessModal.jsx) - nothing in this app is ever emailed.
   const handleResetPassword = () => {
      if (!platformUser || resetting) return;
      setResetting(true);
      setResetError('');
      axios.post('/api/platform-users/' + platformUser.id + '/reset-password', {}, { withCredentials: true })
         .then(response => {
            setResetLink(response.data.resetPasswordLink || '');
            setCopied(false);
         })
         .catch(() => setResetError(lang.resetPasswordError))
         .finally(() => setResetting(false));
   }
   const handleCopy = () => {
      if (!resetLink) return;
      navigator.clipboard.writeText(resetLink).then(() => {
         setCopied(true);
         setTimeout(() => setCopied(false), 1500);
      }).catch(() => {});
   }

   // Reuses UserPlatform.active as-is (already the exact field checkLogin()
   // rejects on server-side, per PlatformUserController's own PUT contract) -
   // this is purely a new UI on top of an existing, already-enforced flag,
   // not a new capability on the backend. Optimistic local update, reverted
   // on failure so the checkbox never silently drifts from server state.
   const handleToggleActive = () => {
      if (!platformUser || togglingActive) return;
      const nextActive = !platformUser.active;
      setTogglingActive(true);
      setActiveError('');
      axios.put('/api/platform-users/' + platformUser.id, JSON.stringify({ active: String(nextActive) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      })
         .then(() => setPlatformUser((prev) => ({ ...prev, active: nextActive })))
         .catch(() => setActiveError(lang.accountActiveError))
         .finally(() => setTogglingActive(false));
   }

   return (
      <div className='student-lessons'>
         <p className='student-card-section-title'>{lang.actionsTitle}</p>
         <div className='platform-actions-toolbar'>
            <div className='platform-actions-toolbar-tools'>
               <span
                  className={'platform-status-pill toggle-clickable' + (platformUser && platformUser.active ? ' on' : '') + (togglingActive ? ' toggle-pending' : '')}
                  onClick={handleToggleActive}
                  title={lang.accountActiveLabel}>
                  <i className='platform-status-dot'></i>
                  {platformUser && platformUser.active ? lang.accountStatusOn : lang.accountStatusOff}
               </span>
               <button type='button' className='platform-tool-btn' disabled={!platformUser || resetting || !platformUser.active} onClick={handleResetPassword}>
                  <IconKeys style={{ width: 16, height: 16 }} />
                  <span>{resetting ? topLang['Loading ...'] : lang.resetPasswordButton}</span>
               </button>
            </div>
            {activeError ? <p className='platform-modal-error'>{activeError}</p> : ''}
            {resetError ? <p className='platform-modal-error'>{resetError}</p> : ''}
            {resetLink ? (
               <div className='platform-actions-toolbar-result'>
                  <div className='platform-modal-link-box'>
                     <span className='platform-modal-link-text'>{resetLink}</span>
                     <div className='platform-modal-copy-btn' onClick={handleCopy} title={topLang.copyActivationLink}>
                        <IconCopyUrl style={{ width: 15, height: 15, fill: 'var(--bg-color)' }} />
                     </div>
                     {copied ? <span className='platform-modal-copied-hint'>{topLang.studentsPage.copied}</span> : ''}
                  </div>
                  <p className='platform-modal-hint'>{lang.resetPasswordHint}</p>
               </div>
            ) : ''}
         </div>

         <p className='student-card-section-title'>Повідомлення</p>
         <div className='platform-notification-add-row'>
            <button type='button' className='platform-action-btn' onClick={openPicker}>
               <IconAdd style={{ width: 14, height: 14, margin: 0 }} />
               Додати існуючий
            </button>
            <button type='button' className='platform-action-btn' onClick={() => setShowCreateModal(true)}>
               <IconAdd style={{ width: 14, height: 14, margin: 0 }} />
               Створити новий
            </button>
         </div>
         {showPicker ? (
            <div className='platform-notification-picker'>
               <select className='submit-field form-selection txt-c' value={pickerValue} onChange={(e) => setPickerValue(e.target.value)}>
                  <option value=''>Оберіть повідомлення...</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
               </select>
               <button type='button' className='platform-action-btn' disabled={!pickerValue} onClick={handleAssignExisting}>Додати</button>
               <button type='button' className='platform-action-btn' onClick={() => setShowPicker(false)}>Скасувати</button>
            </div>
         ) : ''}
         <div className='platform-notification-list'>
            {notifications.length === 0 ? (
               <p className='student-platform-empty-hint'>У студента ще немає жодного повідомлення</p>
            ) : ''}
            {notifications.map(sn => {
               const notif = sn.notification || {};
               const t = getNotificationType(notif.type);
               const needsAcceptance = notif.type === 'ACK_REQUIRED' || notif.type === 'BLOCKING';
               return (
                  <div className='platform-notification-row' key={sn.id}>
                     <div className='platform-notification-main'>
                        <span className='platform-notification-title'>{notif.title}</span>
                        <div className='platform-notification-meta'>
                           {t ? <span className='exercise-templates-badge' style={{ '--type-accent': t.accent }}>{t.label}</span> : ''}
                           {needsAcceptance ? (
                              <span className={'platform-notification-status ' + (sn.accepted ? 'accepted' : 'pending')}>
                                 {sn.accepted ? 'Підтверджено' : 'Не підтверджено'}
                              </span>
                           ) : ''}
                           <span>Додано: {sn.dAdd ? sn.dAdd.replace('T', ' ').slice(0, 16) : '—'}</span>
                           {sn.accepted ? <span>Підтверджено: {sn.dAccepted ? sn.dAccepted.replace('T', ' ').slice(0, 16) : '—'}</span> : ''}
                        </div>
                     </div>
                     <div className='platform-notification-actions'>
                        <div className='student-lessons-icon-btn' onClick={() => navigate('/communications/' + notif.id)} title='Редагувати'>
                           <IconEdit style={{ width: 14, height: 14 }} />
                        </div>
                        <div className='student-lessons-icon-btn' onClick={() => handleUnassign(sn.id)} title='Прибрати'>
                           <IconDelete style={{ width: 14, height: 14 }} />
                        </div>
                     </div>
                  </div>
               );
            })}
         </div>
         <AssignNewCommunicationModal isOpen={showCreateModal} studentId={id} onClose={() => setShowCreateModal(false)} onAssigned={handleAssignedNew} />

         <p className='student-card-section-title'>{lang.pendingRequestsTitle}</p>
         <div className='platform-request-list'>
            {status === 'done' && requests.length === 0 && negotiationItems.length === 0 ? (
               <p className='student-platform-empty-hint'>{lang.noPendingRequests}</p>
            ) : ''}
            {requests.map(req => (
               <div className='platform-request-row' key={req.id}>
                  <span className='platform-request-text'>{req.message}</span>
                  <div className='platform-request-actions'>
                     <div className='student-lessons-icon-btn platform-request-approve' onClick={() => handleApprove(req.id)} title={lang.approve}>
                        <IconDone style={{ width: 14, height: 14 }} />
                     </div>
                     <div className='student-lessons-icon-btn platform-request-reject' onClick={() => handleReject(req.id)} title={lang.reject}>
                        <IconWrong style={{ width: 14, height: 14 }} />
                     </div>
                  </div>
               </div>
            ))}
            {negotiationItems.map((item) => {
               const isReschedule = item.type === 'reschedule';
               const raw = item.raw;
               const lesson = isReschedule ? (raw.lesson || {}) : ((raw.event || {}).lesson || {});
               const studentName = isReschedule ? (raw.initiatedBy ? (raw.initiatedBy.name + ' ' + raw.initiatedBy.surname) : '') : (raw.student ? (raw.student.name + ' ' + raw.student.surname) : '');
               const dayPart = isReschedule ? dayParts.find((dp) => dp.id === raw.currentProposedDayPart) : null;
               const summary = isReschedule
                  ? studentName + ' пропонує перенести заняття ' + (lesson.date || '') + (raw.currentProposedDate ? ' на ' + raw.currentProposedDate : '') + (dayPart ? ' (' + dayPart.label + ')' : '')
                  : studentName + ' просить скасувати заняття ' + (lesson.date || '') + (lesson.start ? ', ' + lesson.start : '');
               return (
                  <div className='platform-request-row reschedule-request-row toggle-clickable' key={item.type + '-' + item.id}
                     onClick={() => setRescheduleModalItem(item)}>
                     <span>
                        <span className={'platform-request-type-badge type-' + item.type}>{isReschedule ? 'Зміна терміну' : 'Скасування'}</span>
                        <span className='platform-request-text'>{summary}</span>
                     </span>
                  </div>
               );
            })}
         </div>

         <RescheduleResponseModal key={rescheduleModalItem ? rescheduleModalItem.type + '-' + rescheduleModalItem.id : 'none'}
            isOpen={!!rescheduleModalItem} item={rescheduleModalItem}
            onClose={() => setRescheduleModalItem(null)}
            onResolved={loadNegotiations} />
      </div>
   );
}
